import { useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Send this to the group chat.
 *
 * # The link is always on screen as text
 *
 * `navigator.share` and `navigator.clipboard` are both undefined outside a
 * secure context, which is exactly the LAN address a party on a home network is
 * served from. A share button that silently does nothing is worse than no share
 * button — so the URL is the feature and the controls are the bonus. Phase 7
 * made the same call for the party invite.
 *
 * # And it is the page's own URL
 *
 * Not rebuilt from a game id and a remembered format. The page is already at the
 * address somebody would share; deriving it a second way is a second place for
 * it to be wrong.
 *
 * Deliberately **not** `appUrl` (phase 11), unlike the display's recap QR: that
 * one is rendered by a screen that may be running from a package with no origin
 * of its own, and this one cannot be — a recap is a served page or it does not
 * exist. Asking `serverOrigin()` here would replace a fact with a lookup.
 */
export function ShareLink() {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const url = window.location.href;

  return (
    <section
      aria-label={t("share.label")}
      className="rounded-lg border border-white/15 p-3"
    >
      <h2 className="text-sm font-semibold">{t("share.heading")}</h2>
      <p className="mt-2 break-all font-mono text-xs text-flash-400">{url}</p>
      <div className="mt-2 flex gap-2">
        {typeof navigator.share === "function" && (
          <button
            type="button"
            onClick={() => {
              void navigator.share({ url, title: t("board.howItWent") }).catch(() => {
                // Dismissed, or refused without a gesture the browser
                // recognised. The link is right there.
              });
            }}
            className="min-h-9 rounded-lg border border-white/20 px-3 text-xs font-medium"
          >
            {t("share.action")}
          </button>
        )}
        {navigator.clipboard && (
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard.writeText(url).then(
                () => {
                  setCopied(true);
                },
                () => {
                  // Same as above.
                },
              );
            }}
            className="min-h-9 rounded-lg border border-white/20 px-3 text-xs font-medium"
          >
            {copied ? t("setup.copied") : t("setup.copy")}
          </button>
        )}
      </div>
    </section>
  );
}
